import { createFileRoute, Outlet } from "@tanstack/react-router";
import { AdminSidebar } from "@/components/admin/admin-sidebar";
import { requireView } from "@/core/lib/guards";
import { SessionProvider } from "@/core/lib/session-context";
import { ImpersonationBanner } from "@/ui/components/auth/impersonation-banner";
import { TopHeader } from "@/ui/components/dashboard/top-header";
import { GuardedShellPending } from "@/ui/components/skeletons/route-pending";
import { SidebarInset, SidebarProvider } from "@/ui/components/ui/sidebar";

/**
 * The platform-admin shell — the port of the `(admins)` server layout.
 *
 * The guard runs in `beforeLoad`, so it resolves before anything below renders:
 * "admin" only resolves for accounts whose role is actually admin or moderator,
 * which both enforces the boundary (everyone else is bounced to their own home,
 * across origins if that home is the customer app) and lets staff who switched
 * to another surface be redirected there.
 *
 * Moderators additionally only see/reach their granted sections — the sidebar
 * filters on the grants below, each section route re-checks them in its own
 * `beforeLoad`, and every section's API re-checks them server-side.
 */
export const Route = createFileRoute("/_admin")({
  beforeLoad: async ({ context, location }) => {
    const auth = await requireView(context.queryClient, "admin", "/sign-in", location.href);
    // Published to descendants so per-section guards need no second fetch.
    return { auth };
  },
  component: AdminLayout,
  // The guard is a network round trip, and until it lands there is no shell to
  // show. The shell's outline stands in once a session is known — never for a
  // visitor the guard is about to send to `/sign-in`.
  pendingComponent: GuardedShellPending,
});

/** Mirrors the `admin_sidebar_state` cookie the server layout used to read. */
function sidebarDefaultOpen(): boolean {
  if (typeof document === "undefined") return true;
  return !document.cookie.split("; ").some((c) => c === "admin_sidebar_state=false");
}

function AdminLayout() {
  const { auth } = Route.useRouteContext();
  const sections = auth.capabilities.isAdmin ? null : auth.capabilities.moderatorSections;

  return (
    <SessionProvider initial={auth.session}>
      <SidebarProvider defaultOpen={sidebarDefaultOpen()}>
        <AdminSidebar sections={sections} />
        <SidebarInset>
          <TopHeader />
          <ImpersonationBanner session={auth.session} />
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </SessionProvider>
  );
}
